<template>
  <div id="app">

    <link rel="stylesheet" type="text/css" :href="currentThemeFile">

    <Header
      :strings="strings"
      :settings="settings"
      :simulatorController="simulatorController"
      :yas-controller="yasController"
      :hcl-controller="hclController"
      @updateCurrentTab="updateCurrentTab"
      @showPromptDialog="showPromptDialog"
    />

    <Tabs
      ref="Tabs"
      :strings="strings"
      :currentTab="settings.current.tab"
      :tabs="settings.tabs"
      :kernelController="kernelController"
      :hclController="hclController"
      :simulatorController="simulatorController"
      @updateCurrentTab="updateCurrentTab"
      @showPromptDialog="showPromptDialog"
    />

    <PromptDialog
      ref="PromptDialog"
      :scopedStrings="strings.PromptDialog"
    />

  </div>

</template>

<script>
  import Header from './components/Header.vue'
  import PromptDialog from './components/PromptDialog.vue'
  import Tabs from './components/Tabs.vue'

  import settings from '@/assets/json-data/settings.json'

  // Import Controller
  let KernelController = require('.///controllers/kernelController')
  let HclController = require('.///controllers/hclController')
  let YasController = require('.///controllers/yasController')
  let SimulatorController = require('.///controllers/simulatorController')

  let kernelController = new KernelController.KernelController()
  let hclController = new HclController.HclController(kernelController)
  let yasController = new YasController.YasController(kernelController)
  let simulatorController = new SimulatorController.SimulatorController(kernelController)

  export default {
    name: 'App',
    data () {
      return {
        settings,
        strings: {},
        hclController : hclController,
        kernelController : kernelController,
        yasController : yasController,
        simulatorController: simulatorController,
        currentKernelDefaultInstructionSet: [],
        currentKernelDefaultHcl: ""
      }
    },
    components: {
      Header,
      PromptDialog,
      Tabs
    },
    computed: {
      currentKernel () {
        return settings.current.kernel
      },
      currentLanguage () {
        return settings.current.language
      },
      currentThemeFile () {
        return 'css/themes/' + settings.themes[settings.current.theme].filename
      }
    },
    methods: {
      isCurrentTab (tabName) {
        return this.$refs.Tabs.isCurrentTab(tabName)
      },
      getLanguageStrings (filename) {
        return require(`@/assets/json-data/strings/${filename}`)
      },
      loadKernelDefaults () {
        let loadSuccessful = false
        try {
          let kernelName = this.settings.kernels[this.currentKernel]
          let defaultY86 = require(`../ts/model/kernel-${kernelName}/y86.txt`).default
          this.currentKernelDefaultInstructionSet = require(`../ts/model/kernel-${kernelName}/instructionSet.json`)
          this.currentKernelDefaultHcl = require(`../ts/model/kernel-${kernelName}/hcl.txt`).default
          loadSuccessful = true

          this.setY86Code(defaultY86)
          this.$refs.Tabs.changeDefaultInstructionSet(this.currentKernelDefaultInstructionSet)
          this.setHclCode(this.currentKernelDefaultHcl)
        } catch (error) {
          if(loadSuccessful) {
            throw error
          }
          let missingFile = error.message.split("'")[1].split("/")[2]
          throw new Error(`${this.strings.App.detail.missingFile} ${missingFile}`)
        }
      },
      getInstructionSet () {
        return this.$refs.Tabs.getInstructionSet()
      },
      setInstructionSet (newInstructionSet) {
        this.$refs.Tabs.setInstructionSet(newInstructionSet)
      },
      getHclCode () {
        return this.$refs.Tabs.getHclCode()
      },
      setHclCode (code) {
        this.$refs.Tabs.setHclCode(code)
      },
      getY86Code () {
        return this.$refs.Tabs.getY86Code()
      },
      setY86Code (code) {
        this.$refs.Tabs.setY86Code(code)
      },
      showPromptDialog (type, message, detail) {
        this.$refs.PromptDialog.show(type, message, detail)
      },
      simulationCanRun() {
        return this.$refs.Tabs.simulationCanRun()
      },
      updateKernel (newKernel) {
        try {
          this.loadKernelDefaults()
          this.kernelController.useKernel(settings.kernels[newKernel], this.currentKernelDefaultInstructionSet, this.currentKernelDefaultHcl)
          this.$refs.Tabs.resetObjCode()
          this.$refs.Tabs.tightenInterface(this.simulatorController.isInterfaceTightened())
        } catch (error) {
          let kernelName = this.settings.kernels[this.currentKernel]
          this.showPromptDialog('error', `${this.strings.App.kernel} "${kernelName}" ${this.strings.App.loadKernelError}`, error.message)
        }
      },
      updateCurrentTab (newCurrentTab) {
        this.settings.current.tab = newCurrentTab
      },
      initObjCode (compilationResult, highlightRules) {
        this.$refs.Tabs.initObjCode(compilationResult, highlightRules)
      },
      getCompilationResult() {
        return this.$refs.Tabs.getCompilationResult()
      },
      updateObjCodeHighlightRules (newRules) {
        this.$refs.Tabs.updateObjCodeHighlightRules (newRules)
      },
      updateCpuState(newValue) {
        this.$refs.Tabs.updateCpuState(newValue)
      },
      updateRegisters(newValue) {
        this.$refs.Tabs.updateRegisters(newValue)
      },
      updateFlags(newValue) {
        this.$refs.Tabs.updateFlags(newValue)
      },
      updateStatus(newValue) {
        this.$refs.Tabs.updateStatus(newValue)
      },
      updatePerformance(newValue) {
        this.$refs.Tabs.updatePerformance(newValue)
      },
      updateMemory(newValue) {
        this.$refs.Tabs.updateMemory(newValue)
      },
      updateAll() {
        this.updateObjCodeHighlightRules(this.simulatorController.getCurrentKernelHighlightRules())
        this.updateCpuState(this.simulatorController.getCPUStateView())
        this.updateRegisters(this.simulatorController.getRegistersView())
        this.updateFlags(this.simulatorController.getFlagsView())
        this.updateStatus(this.simulatorController.getStatusView())
        this.updatePerformance(this.simulatorController.getPerformanceView())
        this.updateMemory(this.simulatorController.getMemoryView())
      }
    },
    created () {
      // Load all the kernels
      settings.kernels = this.kernelController.getAvailableKernelNames()
      // Load all the languages
      for (let i = 0; i < settings.languages.length; i++) {
        settings.languages[i].strings = this.getLanguageStrings(settings.languages[i].filename)
      }
      // Load the current language in the app
      this.strings = settings.languages[settings.current.language].strings
      document.title = this.strings.documentTitle
    },
    mounted () {
      this.updateKernel(0)
    },
    watch: {
      currentKernel (newKernel) {
        this.updateKernel(newKernel)
        this.updateAll()
      },
      currentLanguage (newLanguage) {
        this.strings = settings.languages[newLanguage].strings
        document.title = this.strings.documentTitle
      }
    }
  }
</script>

<style>
  #app {
    background-color: var(--pane-background);
    color: var(--pane-foreground);
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: fit-content;
    width: 100%;
  }
</style>
